import {Component, OnDestroy, OnInit} from '@angular/core';
import {Product} from "../product";
import {FormControl} from "@angular/forms";
import {ActivatedRoute} from "@angular/router";
import {SearchByPropertyPipe} from "../../../shared/pipes/search-by-property.pipe";
import {IFcDropdownSelectOption} from "../../../shared/components/form-controls/dropdown-select/fc-dropdown-select-option";
import {FilterProductsByStatePipe} from "../filter-products-by-state.pipe";
import {Website} from "../website";
import {SortProductsByFreeInstalledPipe} from "../sort-products-by-free-installed.pipe";
import {Utils} from "../../../shared/utils/utils";
import {PageLoadingBarService} from '../../../shared/services/page-loading-bar/page-loading-bar.service';
import {Permissions} from '../../../user/permissions';
import {ProductService} from '../product.service';
import {WebsiteService} from '../../website.service';
import {UserService} from '../../../user/user.service';
import {ChangelogComponent} from "../changelog/changelog.component";
import {DialogService} from "../../../../ng2-bootstrap-modal/dialog.service";
import {AvailableThemesComponent} from "./available-themes/available-themes.component";
import {UserSubscription} from "../../../user/user-subscription";

import {Store} from '@ngrx/store';
import * as fromStore from '../../../store';
import {Observable, Subscription} from 'rxjs';
import {filter, first, switchMap} from "rxjs/operators";
import {animate, style, transition, trigger} from "@angular/animations";
import {ActivityAction, ActivityStatus, IActivity} from "../../../activities/activity";
import {ProductActionService} from "../product-action.service";
import {DbResponse} from "../../../shared/interceptors/db-response";
import {NotifierComponent} from "../../../shared/components/notifier/notifier.component";
import {Notification} from "../../../shared/components/notifier/notification";
import {NotificationType} from "../../../shared/components/notifier/notification-type";
import {ProductUpdateHistory} from "../productUpdateHistory";
import {TypeUtils} from "../../../shared/utils/type-utils";
import {SortByPipe} from "../../../shared/pipes/sort-by.pipe";
import {User} from "../../../user/user";
import {BuilderTemplates} from "../builder-templates/builder-templates";
import {ThirdPartyThemeActivationDialogComponent} from "./third-party-theme-activation-dialog/third-party-theme-activation-dialog.component";
import {Config} from "../../../config/config";
import {HostingService} from "../hosting/hosting.service";
import {UserSubscriptionType} from "../../../user/user-subscription";
import features from "./themes-features.json";
import {of} from "rxjs";

@Component({
    selector: 'db-themes',
    templateUrl: './themes.component.html',
    styleUrls: ['./themes.component.scss'],
    providers: [SortProductsByFreeInstalledPipe, FilterProductsByStatePipe],
    animations: [
        trigger('productItemAnimation', [
            transition("void => true", [
                style({
                    maxHeight: '0',
                    overflow: 'hidden'
                }),
                animate('500ms ease-out', style({
                    maxHeight: '200px',
                    overflow: 'hidden'
                })),
            ]),
            transition('true => void', [
                style({
                    maxHeight: '200px',
                    overflow: 'hidden'
                }),
                animate('500ms 1000ms ease-out', style({
                    maxHeight: '0',
                    overflow: 'hidden'
                })),
            ]),
        ]),
    ],
    standalone: false
})
export class ThemesComponent implements OnInit, OnDestroy {

  public ActivityAction = ActivityAction;
  public ActivityStatus = ActivityStatus;

  private subscriptions: Subscription[];

  public selectAll: FormControl;

  public bulkAction: FormControl;
  public bulkActionOptions: IFcDropdownSelectOption[];

  public searchTerm: FormControl;

  public filterBy: FormControl;

  public userPermissions: Permissions;
  private _userSubscriptions: UserSubscription[];
  public currentSubscription: UserSubscription;


  public website: Website;
  public themesScope: string;
  public allThemes: Product[];
  public themes: Product[];
  public filteredThemes: Product[];
  public itemsView: FormControl;
  public activeTheme: any;
  public isManagable: boolean;

  public updatingProductsList: boolean;

  public ongoingActivity$: Observable<IActivity>;
  public themeListUpdateLoading: boolean;
  public ongoingActivityChangeTheme$: Observable<IActivity[]>;
  public hasHostingBanner: any;
  public user: User;

  public scheduleCronExpression: string;
  public scheduleEnabled: boolean;
  public scheduleWithBackupEnabled: boolean;
  public themesScheduleLoading: boolean;
  public selectAllForSchedule: FormControl;
  public themesUpdateHistory: ProductUpdateHistory[] = [];
  public themesForSchedule: Product[];
  public backupPlugin: Product;

  public sortDirectionClassName: string = "db-sort-direction-asc";
  private sortByDefaultValue: string = "name";
  private sortDirectionDefault: string = "asc";
  public sortBy: FormControl;
  public sortDirection: FormControl;
  public lastTemplateItem: BuilderTemplates;
  public builderPlugin: Product;
  public ongoingPluginsActivities: any;

  public firstInfoLink: string = "https://help.10web.io/hc/en-us/articles/360016388211-Installing-themes-in-10Web-Dashboard";
  public secondInfoLink: string = "https://help.10web.io/hc/en-us/articles/360010804739-Theme-Scheduled-Updates";
  public UserSubscriptionType: any = UserSubscriptionType;
  public features: string[] = features;
  public demoUser: boolean = false;

  constructor(
    private _route: ActivatedRoute,
    private _pageLoadingBarService: PageLoadingBarService,
    private _userService: UserService,
    private _websiteService: WebsiteService,
    private _productService: ProductService,
    private _hostingService: HostingService,
    private _productActionService: ProductActionService,
    private _searchByPropertyPipe: SearchByPropertyPipe,
    private _sortProductsByFreeInstalledPipe: SortProductsByFreeInstalledPipe,
    private _filterProductsByStatePipe: FilterProductsByStatePipe,
    private _dialogService: DialogService,
    private _sortPipe: SortByPipe,
    private _store: Store<fromStore.DbStoreState>
  ) {}

  ngOnInit(): void {
    this.user = this._route.snapshot.parent.parent.data['user'];

    this.hasHostingBanner = localStorage.getItem("showConnectBanner" + this.user.id);
    this.selectAllForSchedule = new FormControl('');
    this.userPermissions = this._route.snapshot.parent.parent.data['user'].permissions;
    this.website = this._route.snapshot.parent.parent.data['website'];
    this.themesScope = this._route.snapshot.url[0].path;
    this._userSubscriptions = this._route.snapshot.parent.data['subscriptions'];
    this.currentSubscription = this._route.snapshot.parent.data['subscription'];
    this.demoUser = Utils.isDemoUser(this.currentSubscription, this.user);

    this.initBulkActions();
    this.initFilters();
    this.addEventHandlers();

    this.ongoingActivity$ = this._store.select(fromStore.getWebsiteActiveTheme(this.website?.id))
      .pipe(
        switchMap((activeTheme: Product) => activeTheme ? this._store.select(fromStore.getProductOngoingActivity(this.website?.id, activeTheme.slug)) : of(null)),
      );
    this.ongoingActivityChangeTheme$ = this._store.select(fromStore.getOngoingActivities);
    this.ongoingActivityChangeTheme$
      .subscribe((data): void => {
        if (data && data.length) {
          const activateThemeActivity = data.find((activity): boolean => activity.websiteId === this.website?.id && activity.finished && activity.action === ActivityAction.ACTIVATE && activity.status === ActivityStatus.DONE && activity.meta && activity.meta.product_data && activity.meta.product_data.type && activity.meta.product_data.type === "theme");
          const ongoingThemeActivities = data.filter((activity): boolean => activity && this.website && activity.websiteId === this.website?.id && activity.meta && activity.meta.product_data && activity.meta.product_data.type && activity.meta.product_data.type === "theme" && !(activity.status === ActivityStatus.DONE || activity.status === ActivityStatus.FAILED));

          this.ongoingPluginsActivities = data.filter((activity): boolean => activity && this.website && activity.websiteId === this.website?.id && activity.meta && activity.meta.product_data && activity.meta.product_data.type === "plugin" && !(activity.status === ActivityStatus.DONE || activity.status === ActivityStatus.FAILED));
          if (activateThemeActivity) {
            this._productService.updateScreenShot(this.website?.id);
            this._hostingService.clearCache( this.website?.id );
          }
          if ( ongoingThemeActivities && ongoingThemeActivities.length) {
            this.themeListUpdateLoading = true;
          }
          else {
            this.themeListUpdateLoading = false;
          }
        }

      });
  }

  private initBulkActions(): void {
    this.selectAll = new FormControl('');

    this.bulkAction = new FormControl('');
    if (this.themesScope === 'installed') {
      this.bulkActionOptions = [
        {
          value: '',
          label: '-Bulk Action-'
        },
        {
          value: ActivityAction.UPDATE,
          label: 'Update'
        },
        {
          value: ActivityAction.DEACTIVATE_DELETE,
          label: 'Delete'
        }
      ];
    }
    else {
      this.bulkActionOptions = [
        {
          value: '',
          label: '-Bulk Action-'
        },
        {
          value: ActivityAction.INSTALL,
          label: 'Install'
        }
      ];
    }
  }

  private initFilters(): void {
    this.sortBy = new FormControl(this.sortByDefaultValue);
    this.sortDirection = new FormControl(this.sortDirectionDefault);

    this.searchTerm = new FormControl('');
    this.itemsView = new FormControl('list');

    this.filterBy = new FormControl('');
  }


  private filterThemes(): void {
    let filteredThemes: Product[] = <Product[]>this._searchByPropertyPipe.transform(this.themes, {'title': this.searchTerm.value, 'description': this.searchTerm.value});
    filteredThemes = Utils.sort(filteredThemes, (a: Product, b: Product): number => {
      if (this.themesScope === 'installed') {
        return a.active === b.active ? 0 : (b.active && !a.active ? 1 : -1);
      }
      else {
        return a.installed === b.installed ? 0 : (a.installed && !b.installed ? 1 : -1);
      }
    });
    this.filteredThemes = filteredThemes;
    this.isManagable = !this.website.isLocalhost && this.userPermissions.canManageContent;
  }

  public enableSchedulingWithBackup(value) {
    this.scheduleWithBackupEnabled = value.currentTarget.checked;
  }

  private addEventHandlers(): void {
    this.subscriptions = [];

    this._store.select(fromStore.getWebsiteBackupPlugin(this.website?.id))
      .subscribe((backupPlugin): void => {
        if (backupPlugin) {
          this.backupPlugin = backupPlugin;
        }
      });

    this.subscriptions.push(this._store.select(fromStore.getWebsiteThemes(this.website?.id)).subscribe((themes: Product[]) => {
      themes.map((themes, index) => {
        if ( this.themes && this.themes[index] && this.themes[index].selected ) {
          themes.selected = true;
        }
      });
      this.allThemes = themes;
      this.themesForSchedule = TypeUtils.cloneDeep(themes);
      this.getScheduleThemes(true);
      this.getThemesUpdateHistory();
    }));
    this.subscriptions.push(this._store.select(fromStore.getWebsiteActiveTheme(this.website?.id)).subscribe((theme: Product) => {
      this.activeTheme = theme;
    }));

    this.subscriptions.push(this._store.select(fromStore.getLastBuilderTemplate(this.website?.id)).subscribe((builderTemplate: BuilderTemplates) => {
      if (builderTemplate && builderTemplate.id) {
        this.lastTemplateItem = builderTemplate;
      }
    }));

    this.subscriptions.push(this._store.select(fromStore.getWebsiteBuilderPlugin(this.website?.id)).subscribe((builderProduct: Product) => {
      if (builderProduct) {
        this.builderPlugin = builderProduct ? builderProduct : null;
      }
    }));

    this.subscriptions.push(this._store.select(fromStore.getWebsiteInactiveThemes(this.website?.id)).subscribe((themes: Product[]) => {
      if (!this.themes) {
        this.themes = themes;

        this.updatingProductsList = true;
        setTimeout(() => {
          this.filterThemes();

          setTimeout(() => {
            this.updatingProductsList = false;
          });
        });
      }
      else {
        this.themes = themes;
        this.filterThemes();
      }
    }));

    this.subscriptions.push(this.searchTerm.valueChanges.subscribe((): void => {
      this.filterThemes();
    }));

    this.subscriptions.push(this.filterBy.valueChanges.subscribe((): void => {
      this.updatingProductsList = true;
      setTimeout(() => {
        this.filterThemes();

        setTimeout(() => {
          this.updatingProductsList = false;
        });
      });
    }));

    this.subscriptions.push(this.sortBy.valueChanges.subscribe((): void => {
      this.filterHistory();
    }));
  }

  private filterHistory() {
    let filterUpdateHistory = this.themesUpdateHistory ;
    filterUpdateHistory = <any>this._sortPipe.transform(filterUpdateHistory, this.sortBy.value, this.sortDirection.value);
    this.themesUpdateHistory = filterUpdateHistory;
  }

  ngOnDestroy(): void {
    this.removeEventHandlers();
  }

  private removeEventHandlers(): void {
    this.subscriptions.forEach((subscription: Subscription): void => {
      subscription.unsubscribe();
    });
    this.subscriptions = null;
  }

  public toggleAllItemsSelected(selected: boolean): void {
    this.filteredThemes && this.filteredThemes.forEach((theme: Product): void => {
      theme.selected = selected;
    });
  }

  public onItemToggled(): void {
    this.updateSelectAllState();
  }

  private updateSelectAllState(): void {
    this.filteredThemes && this.selectAll.setValue(!this.filteredThemes.find((theme: Product): boolean => !theme.selected));
  }

  public applyBulkAction(bulkaction, deactivate): void {
    const hasLastImportedTemplate: boolean = this.lastTemplateItem && this.lastTemplateItem.id ? true : false;
    const bulkActionItems: Product[] = this.getBulkActionItems(bulkaction);
    if ( hasLastImportedTemplate && bulkaction === ActivityAction.ACTIVATE && this.activeTheme && this.activeTheme.slug === Config.tenWebThemeSlug && bulkActionItems[0].slug !== Config.tenWebThemeSlug) {
      this._dialogService
        .addDialog(ThirdPartyThemeActivationDialogComponent)
        .subscribe((confirmed: boolean) => {
          if ( confirmed ) {
            this.applyBulkActionItems(bulkaction, deactivate);
            if (this.builderPlugin.installed && this.builderPlugin.active) {
              this._store.dispatch(new fromStore.InitWebsiteProductAction({
                website: this.website,
                products: [this.builderPlugin],
                action: ActivityAction.DEACTIVATE,
                network: 0
              }));
            }
          }
        });
    }
    else {
      this.applyBulkActionItems(bulkaction, deactivate);
    }
  }

  public applyBulkActionItems(bulkaction, deactivate): void {
    const bulkActionItems: Product[] = this.getBulkActionItems(bulkaction);
    if (bulkaction) {
      if (bulkActionItems.length) {
        if (deactivate === true) {
          this._store.dispatch(new fromStore.InitWebsiteProductAction({
            website: this.website,
            products: bulkActionItems,
            action: ActivityAction.DEACTIVATE,
            network: 0
          }));
        }
        this._store.dispatch(new fromStore.InitWebsiteProductAction({
          website: this.website,
          products: bulkActionItems,
          action: bulkaction,
          network: 0
        }));
      }
    }
    else {
      if (this.bulkAction.value && bulkActionItems.length) {
        this._store.dispatch(new fromStore.InitWebsiteProductAction({
          website: this.website,
          products: bulkActionItems,
          action: this.bulkAction.value,
          network: 0
        }));
      }
    }

    this.toggleAllItemsSelected(false);
    this.updateSelectAllState();
  }

  private getBulkActionItems(bulkaction): Product[] {
    const bulkAction = bulkaction ? bulkaction : this.bulkAction.value;
    return this.filteredThemes.reduce((bulkActionItems: any[], theme: Product): number[] => {
      if (theme.selected && ( this.filterBy.value === "all" && bulkAction === ActivityAction.DEACTIVATE_DELETE || theme.canApplyAction(bulkAction) ) ) {
        bulkActionItems.push(theme);
      }
      return bulkActionItems;
    }, []);
  }

  public onThemesStateChange($event): void {
    this.toggleAllItemsSelected(false);
    this.updateSelectAllState();
    this.updateSelectAllStateForSchedule();
    this.filterBy.setValue($event.tabId);
  }

  public get selectedThemes(): Product[] {
    if (!this.filteredThemes) {
      return [];
    }

    return this.filteredThemes.filter((theme: Product): boolean => {
      return theme.selected;
    });
  }

  public onAvailableThemes(): void {
    this._dialogService
    .addDialog(AvailableThemesComponent, {
      website: this.website,
      installedfilteredThemes: this.themes,
      pluginScope: this.themesScope,
      userPermissions: this.userPermissions,
      userSubscriptions: this._userSubscriptions,
      currentSubscription: this.currentSubscription,
      user: this.user

    })
    .subscribe((isConfirmed: boolean) => {

    });
  }

  public showChangelog(): void {
    if (this.activeTheme.paidVersionInstalled) {
      this._dialogService
        .addDialog(ChangelogComponent, {
          website: this.website,
          product: this.activeTheme,
          isManagable: this.isManagable
        })
        .subscribe((isConfirmed: boolean) => {});
    }
    else {
      this.update();
    }
  }
  public update(): void {
    this._store.dispatch(new fromStore.InitWebsiteProductAction({
      website: this.website,
      products: [this.activeTheme],
      action: ActivityAction.UPDATE,
      network: 1
    }));
  }

  public productTrackBy(index: number, product: Product) {
    return product.uid;
  }


  public getScheduleThemes(isAction = false): void {

    this.themeListUpdateLoading = true;
    this._productService.getScheduleThemes(this.website?.id)
    .pipe(filter((response: DbResponse): boolean => !!response),
      first())
      .subscribe((response: DbResponse): void => {

        if (response.success) {
          if (response.data) {
            const data = response.data;
            this.scheduleEnabled = this.scheduleEnabled && isAction ? this.scheduleEnabled : data.schedule;
            this.scheduleWithBackupEnabled = data.backup;
            this.scheduleCronExpression = data.cron_expression ? data.cron_expression : '00 00 * * 1';
            this.themesForSchedule.map( (theme) => {
              if (theme.origin === '10web') {
                if (data.tenweb_products.length && data.tenweb_products.includes(theme.productId) || theme.selected == true) {
                  theme.selected = true;
                }
              }
              else {
                if (data.wp_products.length && data.wp_products.includes(theme.slug) || theme.selected == true) {
                  theme.selected = true;
                }
              }
            });
          }
        }
        else {
          NotifierComponent.notify(new Notification(response.message, NotificationType.DANGER));
        }
        this.themeListUpdateLoading = false;

      });
  }

  public getThemesUpdateHistory(): void {
    this._productService.getThemeUpdateHistory(this.website?.id)
      .pipe(filter((response: DbResponse): boolean => !!response),
      first())
      .subscribe((response: DbResponse): void => {

        if (response.success) {
          if (response.data) {
            const data = response.data;
            this.themesUpdateHistory = data;
          }
        }
        else {
          NotifierComponent.notify(new Notification(response.message, NotificationType.DANGER));
        }
      });
  }


  public generateScheduleData(cronExpressionString): void {
    this.scheduleCronExpression = cronExpressionString;
  }

  public enableScheduling(value) {
    this.scheduleEnabled = value.currentTarget.checked;
  }

  public onScheduleSubmit(): void {
    this.themesScheduleLoading = true;
    const wpPlugins = [];
    const tenWebPlugins = [];
    this.selectedThemesForSchedule.map( (theme) => {
      if ( theme.selected ) {
        if (theme.origin === '10web') {
          tenWebPlugins.push(theme.productId);
        }
        else {
          wpPlugins.push(theme.slug);
        }
      }
    });
    const data = {
      cron_expression: this.scheduleCronExpression,
      backup: this.scheduleWithBackupEnabled ? 1 : 0,
      wp_products: JSON.stringify(wpPlugins),
      tenweb_products: JSON.stringify(tenWebPlugins),
      schedule: this.scheduleEnabled ? 1 : 0,
      type: 'theme'
    };
    this.updateSchedule(data);
  }


  public updateSchedule(data) {
    this._productService.setScheduleThemes(this.website?.id, data)
    .pipe(filter((response: DbResponse): boolean => !!response),
      first())
      .subscribe((response: DbResponse): void => {
        this.themesScheduleLoading = false;
        if (response.success) {
          NotifierComponent.notify(new Notification(response.message, NotificationType.SUCCESS));
          this.getScheduleThemes();
        }
        else {
          NotifierComponent.notify(new Notification(response.message, NotificationType.DANGER));
        }
      });
  }

  public toggleAllItemsSelectedForSchedule(selected: boolean): void {
    this.themesForSchedule.forEach((product: Product): void => {
      product.selected = selected;
    });
  }

  public onItemToggledForSchedule(): void {
    this.updateSelectAllStateForSchedule();
  }

  private updateSelectAllStateForSchedule(): void {
    this.selectAllForSchedule.setValue(this.themesForSchedule.length && !this.themesForSchedule.find((theme: Product): boolean => !theme.selected));
  }

  public get selectedThemesForSchedule(): Product[] {
    if (!this.themesForSchedule) {
      return [];
    }

    return this.themesForSchedule.reduce((selectedProducts: Product[], themeWithAddons: any): Product[] => {
      const theme: Product = themeWithAddons;

      if (theme.selected) {
        selectedProducts.push(theme);
      }
      return selectedProducts;
    }, []);
  }

  public getSortDirectionClass(sortBy: string) {
    if (sortBy === this.sortBy.value) {
      return 'db-table-sorting-indicator_active db-sort-direction-' + this.sortDirection.value;
    }
    else {
      return 'db-sort-direction-asc';
    }
  }

  public sortTable(sortBy: string, sortDirectionDefault: string = ""): void {
    let sortDirection: string = this.sortDirection.value;
    if (sortDirectionDefault === "") {
      if (sortBy === this.sortBy.value) {
        sortDirection = (sortDirection === 'asc') ? 'desc' : 'asc';
      }
      else {
        sortDirection = this.sortDirectionDefault;
      }
    } else {
      sortDirection = sortDirectionDefault;
    }

    this.sortDirection.setValue(sortDirection);
    this.sortBy.setValue(sortBy);

    this.sortDirectionClassName = 'db-sort-direction-' + sortDirection;
  }


}
